import type { Agent } from './AgentsSection';

interface ListingAgent {
  id: string;
  name: string;
  email: string;
  avatar: string | null;
  jobTitle: string | null;
}

interface ListingWithAgent {
  listingAgent: ListingAgent | null;
}

const PLACEHOLDER_AVATAR =
  'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=400&h=400';

/**
 * Extract unique agents from an array of listings and convert them
 * to the `Agent` shape used by `AgentsSection` / `FlipAgentCard`.
 *
 * Agents are de-duplicated by ID and sorted by the number of listings
 * they appear on (most active first).
 */
export function extractAgentsFromListings(
  listings: ListingWithAgent[],
): Agent[] {
  const agentMap = new Map<string, { agent: ListingAgent; count: number }>();

  for (const listing of listings) {
    const la = listing.listingAgent;
    if (!la?.id) continue;

    const existing = agentMap.get(la.id);
    if (existing) {
      existing.count += 1;
    } else {
      agentMap.set(la.id, { agent: la, count: 1 });
    }
  }

  return Array.from(agentMap.values())
    .sort((a, b) => b.count - a.count)
    .map(({ agent, count }): Agent => {
      const result: Agent = {
        id: agent.id,
        name: agent.name,
        image: agent.avatar || PLACEHOLDER_AVATAR,
        email: agent.email,
        properties: count,
      };
      if (agent.jobTitle) {
        result.title = agent.jobTitle;
      }
      return result;
    });
}
